// src/utils/cache/static-assets-cache.js
// Static assets preloading cache - leverages browser's native HTTP cache
// Single source of truth for static UI assets preloading (logos, flags, icons)
// Assets are preloaded ONCE at app initialization and cached by the browser
// Versioning support: URLs are automatically versioned with APP_VERSION for cache-busting

import { LOGGING, IMAGE_PATHS, LANGUAGE_SETTINGS, APP_VERSION } from '/settings.js';

/**
 * Static Assets Preloading Cache - Tracks which assets have been preloaded
 * Uses browser's native HTTP cache (not sessionStorage)
 */
const preloadedAssetsCache = new Set();
// Track in-flight requests to prevent duplicate fetches
const inFlightRequests = new Set();

/**
 * Adds version query parameter to URL for cache-busting
 * @param {string} url - The URL to version
 * @returns {string} Versioned URL
 * 
 * EXPORTED: Use this function when setting asset URLs to ensure they match preloaded URLs
 */
export function addVersionToUrl(url) {
    if (!APP_VERSION) return url;

    // Check if URL already has query parameters
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${APP_VERSION}`;
}

/**
 * Preloads a single asset (image or SVG) to leverage browser's native HTTP cache
 * Assets are cached by the browser and persist across sessions until cache is cleared
 * or APP_VERSION changes (which forces a new request)
 * 
 * @param {string} url - The URL of the asset to preload
 * @param {string} type - Asset type ('image' or 'svg')
 * @returns {Promise<void>} Resolves when asset is loaded (or failed)
 */
async function preloadAsset(url, type = 'image') {
    // Add version for cache-busting (same pattern as data-cache.js)
    const versionedUrl = addVersionToUrl(url);

    // Skip if already preloaded or currently fetching
    if (preloadedAssetsCache.has(versionedUrl) || inFlightRequests.has(versionedUrl)) {
        return;
    }

    // Performance Patch: Check if the asset is already in the DOM (e.g. from index.html)
    // This prevents redundant requests for the loading logo, favicon, and other priority assets
    const isAlreadyInDom = () => {
        // Check images
        const images = document.getElementsByTagName('img');
        for (let i = 0; i < images.length; i++) {
            if (images[i].src && images[i].src.includes(versionedUrl)) return true;
        }
        // Check links (preload, icon)
        const links = document.getElementsByTagName('link');
        for (let i = 0; i < links.length; i++) {
            if (links[i].href && links[i].href.includes(versionedUrl)) return true;
        }
        return false;
    };

    if (isAlreadyInDom()) {
        preloadedAssetsCache.add(versionedUrl);
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.log(`[Static Assets Cache] Skipping already in DOM: ${versionedUrl}`);
        }
        return;
    }

    inFlightRequests.add(versionedUrl);

    try {
        if (type === 'svg') {
            // For SVG, use fetch to preload
            const response = await fetch(versionedUrl);
            if (response.ok) {
                preloadedAssetsCache.add(versionedUrl);
                if (LOGGING.ENABLED && LOGGING.CACHE) {
                    console.log(`[Static Assets Cache] Preloaded SVG: ${versionedUrl}`);
                }
            }
        } else {
            // For images, use Image() object for proper preloading
            return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                    preloadedAssetsCache.add(versionedUrl);
                    inFlightRequests.delete(versionedUrl);
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.log(`[Static Assets Cache] Preloaded image: ${versionedUrl}`);
                    }
                    resolve();
                };
                img.onerror = () => {
                    inFlightRequests.delete(versionedUrl);
                    if (LOGGING.ENABLED && LOGGING.CACHE) {
                        console.warn(`[Static Assets Cache] Failed to preload: ${versionedUrl}`);
                    }
                    resolve(); // Resolve anyway to not block other preloads
                };
                img.src = versionedUrl;
            });
        }
    } catch (error) {
        inFlightRequests.delete(versionedUrl);
        if (LOGGING.ENABLED && LOGGING.CACHE) {
            console.warn(`[Static Assets Cache] Error preloading ${versionedUrl}:`, error);
        }
    } finally {
        if (type === 'svg') {
            inFlightRequests.delete(versionedUrl);
        }
    }
}

/**
 * Preloads the loading screen logo PRIORITY (called before loading is shown)
 * This ensures the logo is ready immediately when loading screen appears
 * @returns {Promise<void>} Resolves when logo is preloaded
 */
export async function preloadLoadingLogo() {
    if (!IMAGE_PATHS?.LOGO) {
        return;
    }

    // Preload ONLY the loading logo (highest priority)
    // This is called before showLoading() to ensure logo is ready
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Preloading loading logo (priority)...');
    }

    await preloadAsset(IMAGE_PATHS.LOGO, 'image');
}

/**
 * Preloads logo files
 * @returns {Promise<void>} Resolves when all logos are preloaded
 */
export async function preloadLogos() {
    if (!IMAGE_PATHS?.LOGO) {
        return;
    }

    const logoBasePath = IMAGE_PATHS.LOGO.replace('logo.png', '').replace('logo.webp', '');
    const logosToPreload = [
        // Main logo and favicon are intentionally excluded here because they are handled in index.html
        // and preloaded by the browser's native link preload/icon tags.
        // This avoids redundant JS-initiated fetch calls in the network tab.
        { url: `${logoBasePath}logo_full.webp`, type: 'image' }
    ];

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Preloading secondary logos...');
    }

    await Promise.all(
        logosToPreload.map(logo => preloadAsset(logo.url, logo.type))
    );
}

/**
 * Preloads language flag images
 * @returns {Promise<void>} Resolves when all flags are preloaded
 */
export async function preloadLanguageFlags() {
    if (!IMAGE_PATHS?.FLAGS || !LANGUAGE_SETTINGS?.SUPPORTED_LANGUAGES) {
        return;
    }

    const flagsPath = IMAGE_PATHS.FLAGS;
    const supportedLanguages = LANGUAGE_SETTINGS.SUPPORTED_LANGUAGES || [];
    const showFlags = LANGUAGE_SETTINGS.SHOW_LANGUAGE_FLAGS || {};

    // Only preload flags that are enabled
    const flagsToPreload = supportedLanguages
        .filter(lang => showFlags[lang] !== false)
        .map(lang => ({
            url: `${flagsPath}${lang}.webp`,
            type: 'image'
        }));

    if (flagsToPreload.length === 0) {
        return;
    }

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Preloading language flags...');
    }

    await Promise.all(
        flagsToPreload.map(flag => preloadAsset(flag.url, flag.type))
    );
}

/**
 * Preloads SVG icon sprites (main icons, area icons, cards icons)
 * Uses same path resolution logic as icon.js for consistency
 * @returns {Promise<void>} Resolves when all SVG sprites are preloaded
 */
export async function preloadIconSprites() {
    const svgSprites = [];

    // 1. Main icons sprite (used throughout the app)
    // Match the logic from icon.js: ICONS_SPRITE > ICONS/icons.svg > fallback
    if (IMAGE_PATHS?.ICONS_SPRITE) {
        svgSprites.push({ url: IMAGE_PATHS.ICONS_SPRITE, type: 'svg' });
    } else if (IMAGE_PATHS?.ICONS) {
        const iconsBase = IMAGE_PATHS.ICONS.endsWith('/') ? IMAGE_PATHS.ICONS : `${IMAGE_PATHS.ICONS}/`;
        svgSprites.push({ url: `${iconsBase}icons.svg`, type: 'svg' });
    } else {
        // Fallback to app_core path
        svgSprites.push({ url: '/app_core/src/images/icons/icons.svg', type: 'svg' });
    }

    // 2. Area icons sprite (used for area switcher, etc.)
    // Match the logic from icon.js: AREAS_SPRITE > ICONS/areas.svg > fallback to icons sprite
    if (IMAGE_PATHS?.AREAS_SPRITE) {
        svgSprites.push({ url: IMAGE_PATHS.AREAS_SPRITE, type: 'svg' });
    } else if (IMAGE_PATHS?.ICONS) {
        const iconsBase = IMAGE_PATHS.ICONS.endsWith('/') ? IMAGE_PATHS.ICONS : `${IMAGE_PATHS.ICONS}/`;
        svgSprites.push({ url: `${iconsBase}areas.svg`, type: 'svg' });
    }
    // Note: If no AREAS_SPRITE and no ICONS, we don't add a fallback (areas would use main icons sprite)

    // 3. Cards icons sprite (used for card badges, CTAs, etc.)
    // This is always in app_core (shared across domains)
    svgSprites.push({ url: '/app_core/src/cards/icons/cards-icons.svg', type: 'svg' });

    if (svgSprites.length === 0) {
        return;
    }

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Preloading SVG icon sprites...');
    }

    await Promise.all(
        svgSprites.map(sprite => preloadAsset(sprite.url, sprite.type))
    );
}

/**
 * Preloads all static assets (logos, flags, icon sprites)
 * This should be called early in app initialization
 * @returns {Promise<void>} Resolves when all assets are preloaded
 */
export async function preloadAllStaticAssets() {
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Starting preload of all static assets...');
    }

    // Preload all assets in parallel (they're independent)
    await Promise.all([
        preloadLogos(),
        preloadLanguageFlags(),
        preloadIconSprites()
    ]);

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Finished preloading all static assets');
    }
}

/**
 * Preload custom static assets (for easy extension)
 * @param {Array<{url: string, type: 'image'|'svg'}>} assets - Array of assets to preload
 * @returns {Promise<void>} Resolves when all assets are preloaded
 * 
 * @example
 * // Add custom assets easily:
 * await preloadCustomAssets([
 *     { url: '/images/custom/icon1.png', type: 'image' },
 *     { url: '/images/custom/sprite.svg', type: 'svg' }
 * ]);
 */
export async function preloadCustomAssets(assets) {
    if (!assets || !Array.isArray(assets) || assets.length === 0) {
        return;
    }

    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log(`[Static Assets Cache] Preloading ${assets.length} custom assets...`);
    }

    await Promise.all(
        assets.map(asset => preloadAsset(asset.url, asset.type || 'image'))
    );
}

/**
 * Check if an asset has been preloaded
 * @param {string} url - The URL of the asset (will be versioned automatically)
 * @returns {boolean} True if asset is preloaded
 */
export function isAssetPreloaded(url) {
    const versionedUrl = addVersionToUrl(url);
    return preloadedAssetsCache.has(versionedUrl);
}

/**
 * Clear the preloaded assets cache (for testing/debugging)
 */
export function clearStaticAssetsCache() {
    preloadedAssetsCache.clear();
    inFlightRequests.clear();
    if (LOGGING.ENABLED && LOGGING.CACHE) {
        console.log('[Static Assets Cache] Cleared static assets cache');
    }
}

